import { useCallback, useReducer, useState, useMemo } from 'react'

import {
  Box,
  Button,
  Chip,
  darken,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { animated, useSpring, useTransition } from '@react-spring/web'
import { IconChevronLeft, IconStack2 } from '@tabler/icons-react'

import { navigate, routes } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import AssistantAvatar from 'src/components/Assistant/AssistantAvatar'
import AssistantThinking from 'src/components/Assistant/AssistantThinking'
import GoalChip from 'src/components/Assistant/GoalChip'
import { GET_USER_ASSISTANTS } from 'src/components/Assistant/queries'
import { GENERATE_PROMPTS_FOR_OBJECT } from 'src/components/Assistant/queries'
import Row from 'src/components/Row/Row'
import { CREATE_THREAD } from 'src/components/Threads/mutations'
import { GET_THREADS_FOR_OBJECT_QUERY } from 'src/components/Threads/queries'
import { editorStyle } from 'src/components/Tiptap/styles'
import WorkspaceMemberChip from 'src/components/WorkspaceMemberChip/WorkspaceMemberChip'
import { dayjs } from 'src/lib/dayjs'
import { logger } from 'src/lib/logger'
import { shadows } from 'src/lib/style'

import { standardBoxSpring } from '../spring'
import useObject from '../useObject'

import SidebarHeader from './SidebarHeader'
import SidebarObjectSummary from './SidebarObjectSummary'
import SidebarTabs from './SidebarTabs'
import useSidebar from './useSidebar'

const containerSx = {
  height: '100%',
  overflow: 'auto',

  '& .sidebar-thread': {
    height: 'calc(100% - 120px)',
    overflow: 'hidden',
    position: 'relative',
  },

  '& .thread-message-avatar': {
    height: '24px',
    borderRadius: '100px',
    '& .MuiAvatar-root': {
      height: '24px',
      width: '24px',
    },
    boxShadow: (theme) => shadows[theme.palette?.mode]?.md,
  },
  '& .sidebar-thread-footer': {
    height: '96px',
    width: '686px',
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    right: 16,

    '& .MuiTypography-root': {
      fontSize: '11px',
      fontWeight: 500,
      letterSpacing: '-0.22px',
      cursor: 'pointer',
      opacity: 0.8,
    },

    '& .MuiTextField-root': {
      mt: 1,
      height: '100%',
      backgroundColor: (theme) => theme.palette.background.paper,
    },
  },
  '& .sidebar-assistant': {
    height: 'calc(100vh - 196px)',
    overflow: 'hidden',
    p: 2,

    '& .assistant-dynamic-container': {
      mt: 2,
      width: '100%',
      height: '100%',
      flexShrink: 0,

      '& .responses-container': {
        mt: 1,
        gap: 1,
        width: '100%',
        flexShrink: 0,
        alignItems: 'flex-start',
        pr: '4px',

        '& .MuiAvatar-root': {
          height: '24px',
          width: '24px',
          mt: '14px',
        },
      },
    },

    '& .sidebar-content-mode-threads-inner-suggested-topics-list, .sidebar-previous-threads':
      {
        width: '100%',
        mt: 1,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0, // Reduced from 1 to allow for tight stacking
        mb: 2,
        position: 'relative',
        overflow: 'visible',

        '& .sidebar-suggested-topic, .previous-thread': {
          padding: 1,
          width: '100%',
          backgroundColor: (theme) => theme.palette.background.default,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          cursor: 'pointer',
          fontWeight: 500,
          letterSpacing: '-0.22px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          borderRadius: '50px',
          fontSize: '12px',
          transition: 'background-color 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        },
      },

    '& .previous-thread-container': {
      gap: 1,
      mb: '12px',

      '& .previous-thread': {
        height: '26px',
      },

      '& .MuiChip-root': {
        width: '142px',
      },

      '& .MuiAvatar-root': {
        height: '16px',
        width: '16px',
      },
    },

    '& .sidebar-content-mode-threads-inner-suggested-topics-list': {
      pl: '30px',
      pr: '4px',
    },

    '& .previous-thread': {
      gap: 1,
      '& .MuiTypography-root': {
        fontWeight: 400,
        letterSpacing: '-0.22px',
        fontSize: '11px',
      },
      '& .thread-created-at': {
        width: '100px',
        fontSize: '11px',
      },
      '& .thread-title': {
        fontWeight: 600,
      },
    },
  },

  '& .thread-message': {
    width: '650px',
    mt: 2,
    flexShrink: 0,

    height: 'auto',
    minHeight: '12px',
    background: (theme) => theme.palette.background.paper,
    borderRadius: '6px',
    border: 'none',
    overflow: 'hidden',
    py: 1,
    '&.initialized': {
      boxShadow: (theme) => shadows[theme.palette?.mode]?.md,
    },
    transition: 'box-shadow 1.0s ease-in-out',
  },
  '& .sidebar-thread-container': {
    backgroundColor: (theme) => theme.palette.background.default,
    height: '100%',
    width: '100%',
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    overflowX: 'hidden',
    overflowY: 'auto',

    '& .thread-create-loading-container': {
      height: '100%',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },

    '& .thread-create-loading': {
      height: '64px',
      position: 'relative',
      gap: 1,
      '& .MuiTypography-root': {
        fontWeight: 500,
        letterSpacing: '-0.32px',
        opacity: 0.5,
        lineHeight: '160%',
        fontSize: '13px',
      },
    },
    '& .sidebar-thread-container-inner': {
      height: '100%',
      position: 'relative',
    },
    '& .thread-message': {
      '&:last-of-type': {
        mb: '128px',
      },

      '&.thread-message-user': {
        background: (theme) =>
          `linear-gradient(to bottom, ${darken(theme.palette.background.default, 0.03)}, ${darken(theme.palette.background.default, 0.07)})`,
        alignItems: 'flex-start',
        gap: 1,
        pl: 2,
        '&.initialized': {
          boxShadow: (theme) => shadows[theme.palette?.mode]?.md,
        },
        '& .MuiTypography-root': {
          mt: '2px',
          fontWeight: 600,
          letterSpacing: '-0.32px',
          opacity: 0.8,
          lineHeight: '160%',
          fontSize: '12px',
        },
      },
      '& .thread-message-tiptap-user': {
        background: (theme) =>
          `linear-gradient(to bottom, ${darken(theme.palette.background.default, 0.03)}, ${darken(theme.palette.background.default, 0.07)})`,
        alignItems: 'flex-start',
        gap: 1,
        pl: 2,
        '&.initialized': {
          boxShadow: (theme) => shadows[theme.palette?.mode]?.md,
        },
        '& .thread-message-avatar': {
          height: '24px',
          borderRadius: '100px',
          '& .MuiAvatar-root': {
            height: '24px',
            width: '24px',
          },
          boxShadow: (theme) => shadows[theme.palette?.mode]?.md,
        },
        '& .tiptap.ProseMirror': {
          '& p': {
            mt: '2px',
            fontWeight: 500,
            letterSpacing: '-0.32px',
            opacity: 0.8,
            lineHeight: '160%',
            fontSize: '12px',
          },
        },
      },
      '& .thread-message-tiptap': {
        background: (theme) => theme.palette.background.paper,
        width: '100%',
        pt: '0px',
        px: 3,
        ...editorStyle,
        pb: '0px !important',
        '& .tiptap.ProseMirror': {
          pb: '0px !important',
          '& table': {
            background: (theme) => theme.palette.background.paper,
          },
        },
      },
    },
    '& .thread-messages-container': {
      height: '100%',
      flexDirection: 'column',
      gap: 2,
      width: 'calc(728px + 56px)',
      maxWidth: 'calc(728px + 56px)',

      '& .thread-messages': {
        height: '100%',
        pt: '48px',
        px: 6,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        scrollMarginBottom: '128px',
        '& .thread-messages-loading': {
          mt: 2,
          height: '64px',
          ml: 5,
          position: 'relative',
          gap: 1,
          color: (theme) => theme.palette.text.secondary,
          '& .MuiTypography-root': {
            fontWeight: 500,
            letterSpacing: '-0.32px',
            opacity: 0.7,
            lineHeight: '160%',
            fontSize: '13px',
            color: (theme) => theme.palette.text.secondary,
          },
        },

        '& .thread-messages-end-divider': { height: '128px', flexShrink: 0 },
        '& .thread-messages-end-divider.ai-drafting': {
          height: 'calc(100vh - 168px)',
          flexShrink: 0,
        },
      },
    },
    '& .thread-messages-loading': {
      height: '100%',
      position: 'relative',
    },
  },
}

const reducer = (_state: any, action: any) => {
  switch (action.type) {
    case 'SET_THREAD':
      return action.payload
    case 'RESET_THREAD':
      return null
  }
}

const SidebarModeThreads = () => {
  const theme = useTheme()
  const { currentUser } = useAuth()
  const { object } = useObject()
  const {
    selectedAssistant,
    closeSidebar,
    setAssistantPrompts,
    prompts,
    assistantObservation,
  } = useSidebar()
  const [_state, dispatch] = useReducer(reducer, null)
  const [tab, setTab] = useState<'previous' | 'new'>('new')
  const [suggestedTopicsHovered, setSuggestedTopicsHovered] = useState(false)

  const { data: threadsForObjectData } = useQuery(
    GET_THREADS_FOR_OBJECT_QUERY,
    {
      variables: {
        objectType: object.objectType,
        objectId: object.objectId,
        workspaceId: object.workspaceId,
      },
    }
  )

  const { data: assistantsData } = useQuery(GET_USER_ASSISTANTS, {
    variables: {
      workspaceId: object.workspaceId,
    },
    skip: !object.workspaceId,
  })

  const assistantsById = useMemo(
    () =>
      assistantsData?.userAssistants.reduce((acc, assistant) => {
        acc[assistant.id] = assistant
        return acc
      }, {}),
    [assistantsData]
  )

  const previousThreads = useMemo(
    () =>
      [...(threadsForObjectData?.threadsForObjectForUser ?? [])]
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )
        .filter(Boolean),

    [threadsForObjectData]
  )

  const { loading: generatePromptsForObjectLoading } = useQuery(
    GENERATE_PROMPTS_FOR_OBJECT,
    {
      variables: {
        objectType: object.objectType,
        objectId: object.objectId,
        workspaceId: object.workspaceId,
        assistantId: selectedAssistant?.id,
      },
      onCompleted: (data) => {
        setAssistantPrompts(
          data.generatePromptsForObject.questions,
          data.generatePromptsForObject.keyObservation
        )
      },
      skip:
        !object.workspaceId ||
        !object.objectType ||
        !object.objectId ||
        prompts?.length > 0 ||
        !!assistantObservation,
    }
  )

  const allItems = useMemo(() => {
    // Start with just the prompts
    const result = (prompts ?? []).map((prompt) => ({
      prompt,
      type: 'prompt',
    }))

    // Add previous threads link if there are any
    if (previousThreads?.length > 0) {
      result.push({
        prompt: `See ${previousThreads.length} previous thread${
          previousThreads.length === 1 ? '' : 's'
        }`,
        type: 'previous-threads',
      })

      logger.dev('previousThreads', { previousThreads })
    }

    // Add the text field as the last item
    result.push({
      prompt: '',
      type: 'text-input',
    })

    return result
  }, [prompts, previousThreads])

  // Create a memoized version of the animation properties to prevent unnecessary rerenders
  // Using y (vertical position) as the primary animation for the delightful bouncy effect
  // We don't need this separate config anymore since we're directly configuring the useTrail hook

  // Calculate which items to show and their positions
  const visibleItems = useMemo(() => {
    let yOffset = 0

    return allItems.map((item, index) => {
      // When hovered, show all items with proper spacing
      // When not hovered, only show the first item at position 0
      const isVisible = suggestedTopicsHovered || index === 0

      // Calculate position
      const y = suggestedTopicsHovered ? yOffset : 0

      // For each visible item, increase the offset for the next item
      if (suggestedTopicsHovered) {
        yOffset += 48 // 48px spacing between items
      }

      return {
        ...item,
        isVisible,
        y,
        index,
        height: 36, // Fixed height for all items
      }
    })
  }, [allItems, suggestedTopicsHovered])

  // Animate transitions using useTransition
  const transitions = useTransition(
    // Only include items that should be visible based on hover state
    visibleItems.filter((item) => item.isVisible),
    {
      key: (item) => `item-${item.index}`,
      from: { opacity: 0, transform: 'translateY(0px)' },
      enter: (item) => ({
        opacity: 1,
        transform: `translateY(${item.y}px)`,
      }),
      update: (item) => ({
        opacity: 1,
        transform: `translateY(${item.y}px)`,
      }),
      leave: { opacity: 0, transform: 'translateY(0px)' },
      config: { tension: 280, friction: 20, mass: 1 },
      trail: 25, // Stagger the animations
    }
  )

  const [createThread, { loading: createThreadLoading }] =
    useMutation(CREATE_THREAD)

  const handleCreateThread = useCallback(
    async (prompt: string) => {
      const { data } = await createThread({
        variables: {
          workspaceId: object.workspaceId,
          userPrompt: prompt,
          assistantId: selectedAssistant?.id,
          sourceObject: {
            objectId: object.objectId,
            objectType: object.objectType,
            workspaceId: object.workspaceId,
          },
          dismissedObjects: [],
          contextObjects: [],
        },
      })

      //dispatch({ type: 'SET_THREAD', payload: data.createThread })

      navigate(
        routes.thread({
          threadId: data.createThread.id,
        })
      )

      closeSidebar()
    },
    [
      createThread,
      object.workspaceId,
      object.objectId,
      object.objectType,
      selectedAssistant?.id,
      closeSidebar,
    ]
  )

  const [observationSpring] = useSpring(() => standardBoxSpring, [])

  const handleSelectThread = useCallback(
    (threadId: string) => {
      const thread = threadsForObjectData?.threadsForObjectForUser.find(
        (thread) => thread.id === threadId
      )
      dispatch({ type: 'SET_THREAD', payload: thread })
    },
    [threadsForObjectData]
  )

  const threads = threadsForObjectData?.threadsForObjectForUser || []

  return (
    object && (
      <Box
        className="sidebar-content-mode-view"
        sx={containerSx}
      >
        <SidebarHeader />
        <SidebarTabs />
        <Box className="sidebar-assistant">
          <Box>
            {tab === 'previous' ? (
              <>
                <Row
                  gap={1}
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="h4">Previous Threads</Typography>
                  <Button
                    onClick={() => setTab('new')}
                    startIcon={<IconChevronLeft size={16} />}
                    variant="text"
                    size="small"
                  >
                    Back to Assistant
                  </Button>
                </Row>
                <Box className="sidebar-previous-threads">
                  {(threads ?? []).filter(Boolean).map((thread) => (
                    <Row
                      key={thread.id}
                      onClick={() => handleSelectThread(thread.id)}
                      className="previous-thread-container"
                    >
                      <Chip
                        icon={
                          <AssistantAvatar
                            assistant={assistantsById?.[thread.assistantId]}
                          />
                        }
                        label={dayjs(thread.createdAt).fromNow()}
                        size="small"
                        variant="outlined"
                      />
                      <Row className="previous-thread">
                        <Typography className="thread-title">
                          {thread.title}
                        </Typography>
                      </Row>

                      <GoalChip
                        goalId={thread.assistantGoal}
                        assistant={assistantsById?.[thread.assistantId]}
                      />
                    </Row>
                  ))}
                </Box>
              </>
            ) : tab === 'new' ? (
              <>
                <SidebarObjectSummary
                  object={object}
                  assistant={selectedAssistant}
                />
                <Box>
                  {generatePromptsForObjectLoading ? (
                    <Row className="thread-creation-loading">
                      <AssistantThinking assistant={selectedAssistant} />
                    </Row>
                  ) : (
                    <Box className="assistant-dynamic-container">
                      {assistantObservation && (
                        <Box className="sidebar-object-summary">
                          <Box className="thread-message-avatar">
                            <AssistantAvatar assistant={selectedAssistant} />
                          </Box>

                          <animated.div style={observationSpring}>
                            <Box className="thread-message initialized summary-message">
                              <Typography
                                className="sidebar-object-summary-property-text"
                                variant="body1"
                              >
                                {assistantObservation}
                              </Typography>
                            </Box>
                          </animated.div>
                        </Box>
                      )}
                      <Row className="responses-container">
                        <Box
                          className="sidebar-content-mode-threads-inner-suggested-topics-list"
                          onMouseEnter={() => setSuggestedTopicsHovered(true)}
                          onMouseLeave={() => setSuggestedTopicsHovered(false)}
                          sx={{
                            // Container should be tall enough to fit all items when expanded
                            height: suggestedTopicsHovered
                              ? `${allItems.length * 48}px` // Height accommodates all items
                              : '48px', // Only fits one item when collapsed
                            position: 'relative', // Required for absolute positioning of children
                            overflow: 'visible', // Allow items to be visible outside container
                            marginBottom: 2, // Add space below container
                            transition: 'min-height 0.3s ease-out', // Smooth container height transition
                          }}
                        >
                          {transitions((style, item) => {
                            const promptContent = item.prompt

                            return (
                              <animated.div
                                key={`topic-${item.index}`}
                                style={{
                                  position: 'absolute',
                                  width: 'calc(100% - 36px)',
                                  left: '30px',
                                  top: 0,
                                  flexShrink: 0,
                                  zIndex: allItems.length - item.index,

                                  // Apply the spring-animated styles
                                  ...style,

                                  // Static styling
                                  borderRadius: '50px',
                                  /*boxShadow: suggestedTopicsHovered
                                    ? shadows[theme.palette?.mode]?.sm
                                    : 'none',*/
                                  height: '36px',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                {item.type === 'text-input' ? (
                                  <TextField
                                    fullWidth={true}
                                    variant="standard"
                                    placeholder={`Ask ${selectedAssistant?.firstName || 'Day.ai'} anything...`}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleCreateThread(e.target.value)
                                      }
                                    }}
                                    disabled={createThreadLoading}
                                    sx={{
                                      backgroundColor:
                                        theme.palette.background.default,
                                      borderRadius: '50px',
                                      //boxShadow: shadows[theme.palette?.mode]?.md, // Always has shadow when visible
                                      '&:hover': {
                                        backgroundColor:
                                          theme.palette.background.paper,
                                      },
                                      py: '0px',
                                      height: '36px',
                                    }}
                                    slotProps={{
                                      input: {
                                        disableUnderline: true,
                                        sx: {
                                          border: (theme) =>
                                            `1px solid ${theme.palette.divider}`,
                                          borderRadius: '50px',
                                          py: '4px',
                                          px: '16px',
                                          fontSize: '12px',
                                          fontWeight: 500,
                                          letterSpacing: '-0.22px',
                                          color: (theme) =>
                                            theme.palette.text.primary,
                                          opacity: 0.8,
                                          '& ::placeholder': {
                                            opacity: 0.9,
                                            color: (theme) =>
                                              theme.palette.text.primary,
                                          },
                                        },
                                      },
                                    }}
                                  />
                                ) : item.type === 'prompt' ? (
                                  <Typography
                                    className="sidebar-suggested-topic"
                                    onClick={() =>
                                      handleCreateThread(item.prompt)
                                    }
                                  >
                                    {promptContent}
                                  </Typography>
                                ) : (
                                  <Typography
                                    className="sidebar-suggested-topic"
                                    onClick={() => setTab('previous')}
                                  >
                                    <IconStack2
                                      size={16}
                                      style={{
                                        display: 'inline-block',
                                        verticalAlign: 'middle',
                                        marginRight: '4px',
                                      }}
                                    />
                                    {promptContent}
                                  </Typography>
                                )}
                              </animated.div>
                            )
                          })}

                          {/* Added a div to ensure container has min-height even when no items are shown */}
                          <div style={{ height: 1 }} />
                        </Box>
                        <WorkspaceMemberChip
                          userId={currentUser?.id}
                          workspaceId={object.workspaceId}
                          variant="avatar"
                        />
                      </Row>
                    </Box>
                  )}

                  {createThreadLoading && (
                    <Row className="thread-creation-loading">
                      <AssistantThinking
                        assistant={selectedAssistant}
                        message="Good call, let's discuss..."
                      />
                    </Row>
                  )}
                </Box>
              </>
            ) : (
              <></>
            )}
          </Box>
        </Box>
      </Box>
    )
  )
}

export default SidebarModeThreads
